import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { httpService } from "src/services/http.service";
import { Config } from "@eagerdog/config";
import { Constants } from "@eagerdog/constants";
import { download } from "src/components/Download/DownloadManager";
import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import Pagination from "src/components/Pagination/Pagination";

import styles from "./UserTransactionsTab.module.scss";

interface IProps {
  userId: string;
}

interface IRow {
  _id: string;
  created_at: string;
  status: string;
  statusTone: string;
  payment_type: string;
  entry_amount: string;
  eagerdog_fees: string;
  stripe_fees: string;
  total_charged: string;
  type: string;
  name: string;
  href?: string;
  transaction: any;
}

const PAGE_SIZES = [5, 10, 20];

const formatLabel = (text: string): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatPaymentType = (type: string): string => {
  switch (type) {
    case "pay_club_directly": return "Pay Club Directly";
    case "card": return "Credit Card";
    case "bank_account": return "Bank Account";
    case "cash": return "Cash";
    case "cheque": return "Cheque";
    case "e_transfer": return "E-Transfer";
    default: return formatLabel(type || "Unknown");
  }
};

const statusDisplay = (status: string): { label: string; tone: string } => {
  switch (status) {
    case Constants.transaction_status.unpaid:
      return { label: "Unpaid", tone: "unpaid" };
    case Constants.transaction_status.processing:
      return { label: "Processing", tone: "processing" };
    case "cancelled":
      return { label: "Cancelled", tone: "cancelled" };
    case "disputed":
      return { label: "Disputed", tone: "disputed" };
    case "dispute_lost":
      return { label: "Dispute Lost", tone: "disputeLost" };
    default:
      return { label: "Paid", tone: "paid" };
  }
};

const transactionHref = (transaction: any): string | undefined => {
  if (transaction.registration_type === Constants.registration_type.event && transaction.event?.handle) {
    return `/e/${transaction.event.handle}`;
  }
  if (transaction.registration_type === Constants.registration_type.course && transaction.course?.handle) {
    return `/c/${transaction.course.handle}`;
  }
  if (
    (transaction.registration_type === Constants.registration_type.store ||
      transaction.registration_type === Constants.registration_type.donation) &&
    transaction.club_id
  ) {
    return `/k/${transaction.club_id}`;
  }
  return undefined;
};

const UserTransactionsTab: React.FC<IProps> = ({ userId }) => {
  const [rows, setRows] = useState<IRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const getTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setFailed(false);
      const query = {
        sort: [{
          attribute_name: "created_at",
          sort: "desc"
        }],
        query: {
          $and: [{
            attribute_name: "status",
            attribute_value: {
              operator: "$in",
              value: [
                Constants.transaction_status.success,
                Constants.transaction_status.unpaid,
                Constants.transaction_status.processing,
              ]
            }
          }]
        }
      };

      const response = await httpService.post(
        `${Config.web.REACT_APP_API_URL}/api/users/${userId}/transactions/actions/search`,
        query
      );

      const nextRows: IRow[] = response.data.map((transaction: any) => {
        const formatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: transaction.amounts.currency?.toUpperCase() || "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        const amounts = transaction.amounts || {};
        const entryCents = Math.max(0, (amounts.calculated_base_amount || 0) - (amounts.calculated_discount_amount || 0));
        const eagerDogCents = (amounts.calculated_application_fee || 0) + (amounts.calculated_processing_tax || 0);
        const processedStripe = amounts.processed_processing_fee ?? 0;
        const calculatedStripe = amounts.calculated_processing_fee ?? 0;
        const stripeCents = processedStripe > 0 ? processedStripe : calculatedStripe;
        const totalCents = amounts.processed_total_amount || amounts.calculated_total_amount || 0;

        let name = "Unknown";
        if (transaction.registration_type === Constants.registration_type.event && transaction.event) {
          name = transaction.event.title;
        } else if (transaction.registration_type === Constants.registration_type.course && transaction.course) {
          name = transaction.course.title;
        } else if (transaction.registration_type === Constants.registration_type.store) {
          name = transaction.club?.club_name || "Store Purchase";
        } else if (transaction.registration_type === Constants.registration_type.donation) {
          name = transaction.club?.club_name || "Donation";
        }

        const status = statusDisplay(transaction.status);

        return {
          _id: transaction._id,
          created_at: moment(transaction.created_at).format("MMM D, YYYY [at] h:mm A"),
          status: status.label,
          statusTone: status.tone,
          payment_type: formatPaymentType(amounts.payment_method_type),
          entry_amount: formatter.format(entryCents / 100),
          eagerdog_fees: formatter.format(eagerDogCents / 100),
          stripe_fees: formatter.format(stripeCents / 100),
          total_charged: formatter.format(totalCents / 100),
          type: formatLabel(transaction.registration_type),
          name,
          href: transactionHref(transaction),
          transaction,
        };
      });

      setRows(nextRows);
    } catch (error) {
      console.error("Failed to load transactions:", error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  const downloadReceipt = async (id: string, filename: string) => {
    try {
      const blob = await apiService.downloadStoreReceipt(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download receipt:", error);
      toast.show({
        title: "Error",
        content: "Failed to download receipt. Please try again.",
        type: "fail",
        duration: 5000
      });
    }
  };

  const cancelEntry = async (transaction: any) => {
    try {
      await apiService.cancelUnpaidTransactionSelf(transaction.event_id, transaction._id);
      toast.show({ title: "Entry Cancelled", content: "Your entry has been cancelled.", type: "success", duration: 5000 });
      getTransactions();
    } catch (error) {
      toast.show({ title: "Error", content: "Failed to cancel entry. Please try again.", type: "fail", duration: 5000 });
    }
  };

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  const renderActions = (row: IRow) => {
    const transaction = row.transaction;
    const isUnpaid = transaction?.status === Constants.transaction_status.unpaid;
    const isEvent = transaction?.registration_type === Constants.registration_type.event;
    const isStore = transaction?.registration_type === Constants.registration_type.store;
    const isDonation = transaction?.registration_type === Constants.registration_type.donation;

    if (isUnpaid && isEvent) {
      return (
        <button type="button" className={styles.cancel} onClick={() => cancelEntry(transaction)}>
          Cancel
        </button>
      );
    }

    if (!isUnpaid && isEvent) {
      return (
        <Button
          className={styles.download}
          onClick={() => download.pushDownload({ type: "transaction", transactionId: row._id })}
        >
          Download Entry Form(s)
        </Button>
      );
    }

    if (isStore || isDonation) {
      return (
        <Button
          className={styles.download}
          onClick={() => downloadReceipt(row._id, `${isDonation ? "donation-receipt" : "receipt"}-${row._id}.pdf`)}
        >
          Download Receipt
        </Button>
      );
    }

    return null;
  };

  return (
    <div className={styles.UserTransactionsTab}>
      {loading && <p className={styles.message}>Loading transactions…</p>}
      {!loading && failed && <p className={styles.message}>Transactions could not be loaded.</p>}
      {!loading && !failed && rows.length === 0 && <p className={styles.message}>No transactions yet</p>}
      {!loading && !failed && rows.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={rows.length}
          pageSizeOptions={PAGE_SIZES}
          itemLabel="transactions"
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        >
          <div className={styles.list}>
            {pageRows.map((row) => {
              const actions = renderActions(row);
              return (
                <article key={row._id} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.identity}>
                      <div>
                        <p className={styles.kicker}>{row.type}</p>
                        {row.href ? (
                          <h2><Link to={row.href}>{row.name}</Link></h2>
                        ) : (
                          <h2>{row.name}</h2>
                        )}
                      </div>
                      <span className={`${styles.badge} ${styles[row.statusTone]}`}>{row.status}</span>
                    </div>
                    {actions && <div className={styles.actions}>{actions}</div>}
                  </div>
                  <div className={styles.details}>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Payment type</p>
                      <p className={styles.value}>{row.payment_type}</p>
                    </div>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Entry Amount</p>
                      <p className={styles.value}>{row.entry_amount}</p>
                    </div>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Eager Dog Fees</p>
                      <p className={styles.value}>{row.eagerdog_fees}</p>
                    </div>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Stripe Fees</p>
                      <p className={styles.value}>{row.stripe_fees}</p>
                    </div>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Total Charged</p>
                      <p className={styles.value}>{row.total_charged}</p>
                    </div>
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Transaction Date/Time</p>
                      <p className={styles.value}>{row.created_at}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </Pagination>
      )}
    </div>
  );
};

export default UserTransactionsTab;
